import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { BottomNav } from "./BottomNav";
import { GracePeriodBanner } from "../molecules/GracePeriodBanner";
import { AlertBadge } from "../molecules/AlertBadge";
import { AlertCenterSlideUp } from "./AlertCenterSlideUp";
import { PortalCartFloating } from "../portal/PortalCartFloating";
import { useUiStore } from "../../store/uiStore";
import { useAuthStore } from "../../store/authStore";
import { useReorderAlertStore } from "../../store/reorderAlertStore";
import { getVisibleNavItems } from "../../config/navigation";

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { t } = useTranslation("common");
  const { mobileMenuOpen, setMobileMenuOpen, alertPanelOpen, setAlertPanelOpen } = useUiStore();
  const { user } = useAuthStore();
  const fetchAlertCount = useReorderAlertStore((s) => s.fetchAlertCount);
  const isAdmin = user?.role === "admin";
  const visibleMobileNavItems = useMemo(() => getVisibleNavItems(user?.role), [user?.role]);

  useEffect(() => {
    if (isAdmin) fetchAlertCount();
  }, [isAdmin, fetchAlertCount]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
      {/* Grace Period Banner */}
      <GracePeriodBanner />

      {/* Header */}
      <Header />

      {/* Mobile menu overlay + drawer */}
      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 dark:bg-black/60 z-40"
            aria-hidden
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="md:hidden fixed top-0 start-0 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-neutral-800 border-e border-neutral-200 dark:border-neutral-700 z-50 flex flex-col shadow-xl">
            <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-200 dark:border-neutral-700">
              <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {t("menu")}
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                aria-label={t("close_menu")}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 py-4 overflow-y-auto">
              <ul className="space-y-1 px-2">
                {visibleMobileNavItems.map((item) => (
                  <li key={item.path}>
                    {item.path === "/alerts" ? (
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setAlertPanelOpen(true);
                        }}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg min-h-[44px] w-full text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800"
                      >
                        <item.icon className="w-6 h-6 flex-shrink-0" />
                        <span className="flex items-center gap-2 font-medium">
                          {t(item.labelKey)}
                          <AlertBadge />
                        </span>
                      </button>
                    ) : (
                      <NavLink
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800 ${
                            isActive
                              ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400"
                              : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                          }`
                        }
                      >
                        <item.icon className="w-6 h-6 flex-shrink-0" />
                        <span className="flex items-center gap-2 font-medium">{t(item.labelKey)}</span>
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </>
      )}

      {/* Main content area with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <Sidebar />

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 pb-20 md:pb-4">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNav />

      {/* Mobile slide-up alert center (AC7) */}
      <AlertCenterSlideUp isOpen={alertPanelOpen} onClose={() => setAlertPanelOpen(false)} />

      {/* Portal floating cart (Story 7-4) – customer only */}
      {user?.role === "customer" && <PortalCartFloating />}
    </div>
  );
}
